import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Clock, User, MapPin, Calendar, CheckCircle, Building, Briefcase } from 'lucide-react';
import { appointmentService, userService, workScheduleService } from '../services/api';
import projectHoursService from '../services/projectHoursService';
import toast from 'react-hot-toast';

// Cores por cargo para horas de projeto
const ROLE_COLORS = {
    GERENCIA: { bg: 'bg-purple-100', border: 'border-purple-500', text: 'text-purple-800' },
    RECEPCAO_COMPRAS_AGENDA: { bg: 'bg-blue-100', border: 'border-blue-500', text: 'text-blue-800' },
    RECEPCAO_PROJETOS: { bg: 'bg-indigo-100', border: 'border-indigo-500', text: 'text-indigo-800' },
    PRODUCAO: { bg: 'bg-green-100', border: 'border-green-500', text: 'text-green-800' },
    MONTAGEM_CLIENTE: { bg: 'bg-orange-100', border: 'border-orange-500', text: 'text-orange-800' }
};

const ROLE_LABELS = {
    GERENCIA: 'Gerência',
    RECEPCAO_COMPRAS_AGENDA: 'Compras/Agenda',
    RECEPCAO_PROJETOS: 'Projetos',
    PRODUCAO: 'Produção',
    MONTAGEM_CLIENTE: 'Montagem'
};

const ProjectCalendar = ({ selectedUserId, showAppointments = true, showProjectHours = true }) => {
    const queryClient = useQueryClient();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [viewMode, setViewMode] = useState('all'); // 'all', 'appointments', 'projects'

    // Calcular início e fim do mês atual
    const monthStart = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        return new Date(year, month, 1);
    }, [currentDate]);

    const monthEnd = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        return new Date(year, month + 1, 0, 23, 59, 59);
    }, [currentDate]);

    // Buscar agendamentos do mês
    const { data: appointmentsData, isLoading: loadingAppointments, refetch: refetchAppointments } = useQuery({
        queryKey: ['appointments-calendar', selectedUserId, monthStart.toISOString(), monthEnd.toISOString()],
        queryFn: () => appointmentService.getAppointments({
            responsible_user_id: selectedUserId || '',
            date_from: monthStart.toISOString(),
            date_to: monthEnd.toISOString(),
            limit: 1000
        }),
        enabled: showAppointments && !!monthStart && !!monthEnd,
        staleTime: 0, // Não fazer cache para garantir dados atualizados
        refetchOnWindowFocus: true
    });

    // Buscar horas de projeto agendadas
    const { data: projectHoursData, isLoading: loadingProjectHours, refetch: refetchProjectHours } = useQuery({
        queryKey: ['project-hours-calendar', monthStart.toISOString(), monthEnd.toISOString()],
        queryFn: () => projectHoursService.getScheduledProjects({
            start_date: monthStart.toISOString().split('T')[0],
            end_date: monthEnd.toISOString().split('T')[0]
        }),
        enabled: showProjectHours && !!monthStart && !!monthEnd,
        staleTime: 0, // Não fazer cache para garantir dados atualizados
        refetchOnWindowFocus: true
    });

    // Buscar usuários
    const { data: usersData } = useQuery({
        queryKey: ['users-calendar'],
        queryFn: () => userService.getUsers({ limit: 100 }),
        staleTime: 5 * 60 * 1000
    });

    // Buscar jornadas de trabalho
    const { data: schedulesData } = useQuery({
        queryKey: ['work-schedules-calendar', selectedUserId],
        queryFn: () => workScheduleService.getWorkSchedules({
            user_id: selectedUserId || undefined,
            is_active: true
        }),
        enabled: !!selectedUserId,
        staleTime: 5 * 60 * 1000
    });

    // Normalizar agendamentos
    const appointments = useMemo(() => {
        if (!appointmentsData) return [];
        const data = appointmentsData.data;
        if (Array.isArray(data)) {
            console.log('[Calendar] Agendamentos carregados:', data.length);
            return data;
        }
        if (data && typeof data === 'object' && Array.isArray(data.data)) {
            console.log('[Calendar] Agendamentos carregados (nested):', data.data.length);
            return data.data;
        }
        console.log('[Calendar] Nenhum agendamento encontrado');
        return [];
    }, [appointmentsData]);

    // Normalizar horas de projeto
    const projectHours = useMemo(() => {
        console.log('[Calendar DEBUG] projectHoursData:', JSON.stringify(projectHoursData, null, 2));

        if (!projectHoursData) {
            console.log('[Calendar] projectHoursData vazio');
            return [];
        }

        // O axios já faz o parse do JSON, então projectHoursData.data é o objeto response
        // A estrutura é: { success: true, data: [...] }
        let data = projectHoursData;

        // Se tem .data e .data.data, é uma resposta duplamente aninhada
        if (projectHoursData.data && projectHoursData.data.data && Array.isArray(projectHoursData.data.data)) {
            console.log('[Calendar] Usando projectHoursData.data.data (duplamente aninhado)');
            data = projectHoursData.data.data;
        }
        // Se tem .data e é array, usar direto
        else if (projectHoursData.data && Array.isArray(projectHoursData.data)) {
            console.log('[Calendar] Usando projectHoursData.data');
            data = projectHoursData.data;
        }
        // Se é array direto
        else if (Array.isArray(projectHoursData)) {
            console.log('[Calendar] Usando projectHoursData direto (array)');
            data = projectHoursData;
        }
        // Se é objeto com success e data, usar data
        else if (projectHoursData.success && Array.isArray(projectHoursData.data)) {
            console.log('[Calendar] Usando projectHoursData.success.data');
            data = projectHoursData.data;
        }
        else {
            console.log('[Calendar] Formato não reconhecido');
            return [];
        }

        console.log('[Calendar] Horas de projeto carregadas:', data.length);
        return data;
    }, [projectHoursData]);

    const users = Array.isArray(usersData?.data) ? usersData.data : [];

    // Organizar eventos por data
    const eventsByDate = useMemo(() => {
        console.log('[eventsByDate] showProjectHours:', showProjectHours, 'projectHours.length:', projectHours.length);

        const grouped = {};

        // Agendamentos
        if (showAppointments && appointments.length > 0) {
            console.log('[eventsByDate] Processando', appointments.length, 'agendamentos');
            appointments.forEach(apt => {
                const date = new Date(apt.appointment_date);
                // Usar fuso local para evitar conversão UTC
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                const dateKey = `${year}-${month}-${day}`;

                if (!grouped[dateKey]) grouped[dateKey] = { appointments: [], projectHours: [] };

                grouped[dateKey].appointments.push({
                    id: apt.id,
                    type: 'appointment',
                    title: apt.client_name,
                    time: `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`,
                    duration: apt.duration_minutes,
                    status: apt.status,
                    responsible: apt.responsible_name,
                    address: apt.client_address,
                    notes: apt.notes,
                    phone: apt.client_phone
                });
            });
        }

        // Horas de projeto
        if (showProjectHours && projectHours.length > 0) {
            console.log('[eventsByDate] Processando', projectHours.length, 'horas de projeto');
            projectHours.forEach(ph => {
                console.log('[eventsByDate] Projeto:', ph.project_code, 'de:', ph.start, 'ate:', ph.end);

                if (!ph.start || !ph.end) {
                    console.log('[eventsByDate] Pulando - sem datas');
                    return;
                }

                // As datas vêm como YYYY-MM-DD (texto puro)
                let current = ph.start;
                const end = ph.end;

                // Para cada dia no intervalo
                let dayCount = 0;
                while (current <= end) {
                    console.log('[eventsByDate] Dia:', current);
                    const dateKey = current;

                    if (!grouped[dateKey]) grouped[dateKey] = { appointments: [], projectHours: [] };

                    // Converter strings para números (API retorna strings)
                    // O backend retorna "hours" não "estimated_hours"
                    const estimatedHours = Number(ph.hours) || 0;
                    const hoursPerDayNum = Number(ph.hours_per_day) || (estimatedHours / 5);
                    const totalHoursNum = estimatedHours;

                    grouped[dateKey].projectHours.push({
                        id: ph.id,
                        type: 'project',
                        title: ph.project_code,
                        projectName: ph.project_name,
                        clientName: ph.client_name,
                        role: ph.role,
                        hours: hoursPerDayNum,
                        totalHours: totalHoursNum,
                        assignedTo: ph.assigned_to_name,
                        notes: ph.notes,
                        status: ph.status
                    });

                    // Incrementar para o próximo dia
                    const [y, m, d] = current.split('-').map(Number);
                    const nextDate = new Date(y, m - 1, d + 1);
                    current = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}-${String(nextDate.getDate()).padStart(2, '0')}`;
                }
            });
        }

        console.log('[eventsByDate] grouped keys:', Object.keys(grouped));
        console.log('[eventsByDate] FINAL grouped:', JSON.stringify(grouped, null, 2));

        return grouped;
    }, [appointments, projectHours, showAppointments, showProjectHours]);

    // Gerar dias do mês
    const calendarDays = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const monthName = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'][month];

        console.log('[calendarDays] Gerando para:', monthName, year);

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days = [];

        // Dias do mês anterior
        const prevMonth = new Date(year, month - 1, 0);
        const daysInPrevMonth = prevMonth.getDate();
        for (let i = startingDayOfWeek - 1; i >= 0; i--) {
            days.push({
                date: new Date(year, month - 1, daysInPrevMonth - i),
                isCurrentMonth: false,
                isToday: false
            });
        }

        // Dias do mês atual
        const today = new Date();
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            days.push({
                date,
                isCurrentMonth: true,
                isToday: date.toDateString() === today.toDateString()
            });
        }

        // Dias do próximo mês
        const remainingDays = 42 - days.length;
        for (let day = 1; day <= remainingDays; day++) {
            days.push({
                date: new Date(year, month + 1, day),
                isCurrentMonth: false,
                isToday: false
            });
        }

        return days;
    }, [currentDate]);

    // Navegação
    const goToPreviousMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    };

    const goToCurrentMonth = () => {
        setCurrentDate(new Date());
    };

    const monthNames = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

    const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    const isLoading = loadingAppointments || loadingProjectHours;

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 lg:p-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-1 sm:gap-2">
                    <h2 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">
                        {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h2>
                    {selectedUserId && (
                        <span className="text-xs sm:text-sm text-gray-600">
                            - {users.find(u => u.id === parseInt(selectedUserId))?.full_name || 'Usuário'}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    {/* Botão atualizar */}
                    <button
                        onClick={() => {
                            refetchAppointments();
                            refetchProjectHours();
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Atualizar dados"
                    >
                        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>

                    {/* Filtro de visualização */}
                    <select
                        value={viewMode}
                        onChange={(e) => setViewMode(e.target.value)}
                        className="text-sm border border-gray-300 rounded-lg px-2 py-1"
                    >
                        <option value="all">Tudo</option>
                        <option value="appointments">Só Agendamentos</option>
                        <option value="projects">Só Projetos</option>
                    </select>

                    {/* Navegação */}
                    <button
                        onClick={goToPreviousMonth}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Mês anterior"
                    >
                        <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <button
                        onClick={goToCurrentMonth}
                        className="px-3 py-1 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        Hoje
                    </button>
                    <button
                        onClick={goToNextMonth}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Próximo mês"
                    >
                        <ChevronRight className="w-5 h-5 text-gray-600" />
                    </button>
                </div>
            </div>

            {/* Legenda */}
            <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-blue-500"></div>
                    <span>Agendamentos</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-purple-500"></div>
                    <span>Gerência</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-indigo-500"></div>
                    <span>Projetos</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-green-500"></div>
                    <span>Produção</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded bg-orange-500"></div>
                    <span>Montagem</span>
                </div>
            </div>

            {/* Calendário */}
            {isLoading ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3a7630]"></div>
                </div>
            ) : (
                <>
                    {/* Dias da semana */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {weekDays.map(day => (
                            <div
                                key={day}
                                className="text-center text-xs sm:text-sm font-medium text-gray-600 py-2"
                            >
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Dias do mês */}
                    <div className="grid grid-cols-7 gap-1">
                        {calendarDays.map((day, index) => {
                            // Usar fuso local para evitar conversão UTC
                            const year = day.date.getFullYear();
                            const month = String(day.date.getMonth() + 1).padStart(2, '0');
                            const dayNum = String(day.date.getDate()).padStart(2, '0');
                            const dateKey = day.isCurrentMonth ? `${year}-${month}-${dayNum}` : null;

                            console.log('[render] dateKey:', dateKey, 'isCurrentMonth:', day.isCurrentMonth, 'hasEvents:', dateKey ? !!eventsByDate[dateKey] : 'N/A');

                            const dayEvents = dateKey ? eventsByDate[dateKey] : null;
                            const appointmentCount = dayEvents?.appointments?.length || 0;
                            const projectCount = dayEvents?.projectHours?.length || 0;
                            const totalCount = appointmentCount + projectCount;
                            const isSelected = selectedDate && day.date.toDateString() === selectedDate.toDateString();

                            return (
                                <div
                                    key={index}
                                    className="relative group min-h-[80px] sm:min-h-[100px]"
                                >
                                    <button
                                        onClick={() => day.isCurrentMonth && setSelectedDate(day.date)}
                                        disabled={!day.isCurrentMonth}
                                        className={`
                                            relative p-1 sm:p-2 h-full w-full rounded-lg transition-all text-left
                                            ${!day.isCurrentMonth
                                                ? 'text-gray-300 cursor-not-allowed bg-gray-50'
                                                : day.isToday
                                                ? 'bg-[#f0f7f0] border-2 border-[#49943d] font-semibold'
                                                : isSelected
                                                ? 'bg-[#d9ead9] border-2 border-[#3a7630]'
                                                : 'hover:bg-gray-50 border border-transparent hover:border-gray-200'
                                            }
                                        `}
                                    >
                                        <span className="text-xs sm:text-sm">{day.date.getDate()}</span>

                                        {/* Indicadores de eventos */}
                                        {totalCount > 0 && day.isCurrentMonth && (
                                            <div className="mt-1 space-y-0.5">
                                                {/* Agendamentos */}
                                                {viewMode !== 'projects' && appointmentCount > 0 && (
                                                    <div className="flex items-center gap-0.5">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0"></div>
                                                        <span className="text-[8px] text-gray-600 truncate">
                                                            {appointmentCount}x
                                                        </span>
                                                    </div>
                                                )}

                                                {/* Projetos - mostrar até 2 cargos diferentes */}
                                                {viewMode !== 'appointments' && projectCount > 0 && (
                                                    <div className="space-y-0.5">
                                                        {dayEvents.projectHours.slice(0, 2).map((ph, idx) => {
                                                            const colors = ROLE_COLORS[ph.role] || ROLE_COLORS.PRODUCAO;
                                                            return (
                                                                <div key={idx} className="flex items-center gap-0.5">
                                                                    <div className={`w-1.5 h-1.5 rounded-full ${colors.border.replace('border', 'bg')} flex-shrink-0`}></div>
                                                                    <span className="text-[8px] text-gray-600 truncate">
                                                                        {ph.hours}h {ROLE_LABELS[ph.role]?.substring(0, 4)}
                                                                    </span>
                                                                </div>
                                                            );
                                                        })}
                                                        {projectCount > 2 && (
                                                            <span className="text-[8px] text-gray-400 ml-3">
                                                                +{projectCount - 2} mais
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}

            {/* Detalhes do dia selecionado */}
            {selectedDate && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                            {selectedDate.toLocaleDateString('pt-BR', {
                                weekday: 'long',
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                            })}
                        </h3>
                        <button
                            onClick={() => setSelectedDate(null)}
                            className="text-gray-400 hover:text-gray-600 text-sm"
                        >
                            ✕
                        </button>
                    </div>

                    {(() => {
                        const dateKey = selectedDate.toISOString().split('T')[0];
                        const dayEvents = eventsByDate[dateKey];
                        const appointments = dayEvents?.appointments || [];
                        const projectHours = dayEvents?.projectHours || [];

                        if (appointments.length === 0 && projectHours.length === 0) {
                            return (
                                <div className="text-center py-6 bg-gray-50 rounded-lg">
                                    <Calendar className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500 italic">Nenhum evento neste dia</p>
                                </div>
                            );
                        }

                        return (
                            <div className="space-y-4">
                                {/* Agendamentos */}
                                {viewMode !== 'projects' && appointments.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                            <Calendar className="w-4 h-4 text-blue-500" />
                                            Agendamentos de Clientes ({appointments.length})
                                        </h4>
                                        <div className="space-y-2 max-h-48 overflow-y-auto">
                                            {appointments.map((apt, idx) => (
                                                <div key={`apt-${apt.id}-${idx}`} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                                                    <div className="flex-shrink-0">
                                                        <div className="bg-blue-100 text-blue-700 rounded-lg p-2 text-center min-w-[60px]">
                                                            <div className="text-xs font-semibold">{apt.time}</div>
                                                            <div className="text-[10px] text-blue-600">{apt.duration}min</div>
                                                        </div>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-semibold text-gray-900">{apt.title}</div>
                                                        <div className="text-gray-600 text-xs">{apt.responsible}</div>
                                                        {apt.address && (
                                                            <div className="text-gray-500 text-xs flex items-center gap-1 mt-1">
                                                                <MapPin className="w-3 h-3" />
                                                                {apt.address.substring(0, 30)}...
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Horas de Projeto */}
                                {viewMode !== 'appointments' && dayEvents?.projectHours?.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                                            <Briefcase className="w-4 h-4 text-green-500" />
                                            Projetos ({dayEvents.projectHours.length})
                                        </h4>

                                        {/* Agrupar por projeto para visualização organizada */}
                                        {(() => {
                                            // Agrupar por projeto_id
                                            const projectsGrouped = {};
                                            dayEvents.projectHours.forEach(ph => {
                                                if (!projectsGrouped[ph.project_id]) {
                                                    projectsGrouped[ph.project_id] = {
                                                        projectName: ph.projectName,
                                                        clientName: ph.clientName,
                                                        entries: []
                                                    };
                                                }
                                                projectsGrouped[ph.project_id].entries.push(ph);
                                            });

                                            return (
                                                <div className="space-y-3 max-h-64 overflow-y-auto">
                                                    {Object.entries(projectsGrouped).map(([projectId, project]) => {
                                                        const totalProjectHours = project.entries.reduce((sum, e) => sum + (e.totalHours || 0), 0);
                                                        const totalDayHours = project.entries.reduce((sum, e) => sum + (e.hours || 0), 0);

                                                        return (
                                                            <div key={projectId} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                                                                {/* Header do Projeto */}
                                                                <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                                                                    <div className="flex items-center justify-between">
                                                                        <div>
                                                                            <div className="font-semibold text-gray-900 text-sm">{project.projectName}</div>
                                                                            <div className="text-xs text-gray-500">{project.clientName}</div>
                                                                        </div>
                                                                        <div className="text-right">
                                                                            <div className="text-xs font-semibold text-gray-600">
                                                                                {totalDayHours}h hoje
                                                                            </div>
                                                                            <div className="text-[10px] text-gray-400">
                                                                                Total: {totalProjectHours}h
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Lista de cargos/horas */}
                                                                <div className="p-2 space-y-1.5">
                                                                    {project.entries.map((ph, idx) => {
                                                                        const colors = ROLE_COLORS[ph.role] || ROLE_COLORS.PRODUCAO;
                                                                        return (
                                                                            <div key={`${ph.id}-${idx}`} className={`flex items-center justify-between p-2 rounded-md ${colors.bg} ${colors.border.replace('bg-', 'border-')} border`}>
                                                                                <div className="flex items-center gap-2">
                                                                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors.text} ${colors.bg}`}>
                                                                                        {ROLE_LABELS[ph.role]}
                                                                                    </span>
                                                                                    {ph.assignedTo && (
                                                                                        <span className="text-xs text-gray-500 flex items-center gap-1">
                                                                                            <User className="w-3 h-3" />
                                                                                            {ph.assignedTo}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <div className="flex items-center gap-2">
                                                                                    <span className="text-xs font-semibold text-gray-700">
                                                                                        {ph.hours}h
                                                                                    </span>
                                                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                                                                        ph.status === 'SCHEDULED' ? 'bg-yellow-100 text-yellow-700' :
                                                                                        ph.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-700' :
                                                                                        'bg-gray-100 text-gray-600'
                                                                                    }`}>
                                                                                        {ph.status === 'SCHEDULED' ? 'Agendado' :
                                                                                         ph.status === 'IN_PROGRESS' ? 'Em Andamento' : ph.status}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </div>
            )}
        </div>
    );
};

export default ProjectCalendar;
